/**
 * ConfigTelemetryPage
 *
 * Admin page for QZ usage and seat reporting configuration.
 * Reads/writes organisation/{org_id}/config/telemetry in Firestore via
 * the /admin/config/telemetry endpoints.
 *
 * qz_reporting_topic is read-only — set by QuantumZero
 * at onboarding via setup_apis.py. Only reporting_enabled is customer-editable.
 *
 * Route: /settings/config/telemetry  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Toggle from "../../components/shared/Toggle";
import Loader from "../../components/Loader/Loader";
import { getServiceConfig, updateServiceConfig } from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";

const EMPTY_FORM = {
  qz_reporting_topic:     "",
  reporting_enabled:      false,
  last_reported_at:       null,
  product_name:           "",
  marketplace_product_id: "",
  offer_type:             "",
  env:                    "",
  env_label:              "",
};

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-gray-400 dark:text-ink-gray-500 pb-1 border-b border-edge-gray-200 dark:border-edge-gray-700">
    {children}
  </p>
);

const ConfigTelemetryPage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  // Only reporting_enabled is user-editable/persisted (see handleSave) — the
  // dirty-check below only needs to track this one field, not the whole form.
  const [originalReportingEnabled, setOriginalReportingEnabled] = useState(EMPTY_FORM.reporting_enabled);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("telemetry");
      if (data.configured) {
        setForm({
          qz_reporting_topic:     data.qz_reporting_topic     ?? "",
          reporting_enabled:      data.reporting_enabled      ?? false,
          last_reported_at:       data.last_reported_at       ?? null,
          product_name:           data.product_name           ?? "",
          marketplace_product_id: data.marketplace_product_id ?? "",
          offer_type:             data.offer_type             ?? "",
          env:                    data.env                    ?? "",
          env_label:              data.env_label              ?? "",
        });
        setOriginalReportingEnabled(data.reporting_enabled ?? false);
      } else {
        setForm(EMPTY_FORM);
        setOriginalReportingEnabled(EMPTY_FORM.reporting_enabled);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load telemetry config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Backend strips qz_reporting_topic, last_reported_at —
      // only reporting_enabled is persisted from the UI.
      await updateServiceConfig("telemetry", { reporting_enabled: form.reporting_enabled });
      setOriginalReportingEnabled(form.reporting_enabled);
      showFeedback("Telemetry config saved.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save telemetry config.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPageShell
      title="Telemetry"
      description="Usage and seat reporting to QuantumZero. The reporting topic and seat count are set at onboarding and are read-only here."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">

          {/* QZ Reporting Config — read-only */}
          <div className="flex flex-col gap-4">
            <SectionLabel>QuantumZero Reporting</SectionLabel>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                Reporting Topic <span className="font-normal text-ink-gray-400">(read-only)</span>
              </label>
              <Input
                type="text"
                value={form.qz_reporting_topic || "Not configured"}
                disabled
              />
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                GCP Pub/Sub topic in the QuantumZero project. Set by QuantumZero at onboarding — contact support to update.
              </p>
            </div>

            {form.last_reported_at && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                  Last Reported
                </label>
                <Input
                  type="text"
                  value={new Date(form.last_reported_at).toLocaleString()}
                  disabled
                />
                <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                  Timestamp of the last usage snapshot sent to QuantumZero.
                </p>
              </div>
            )}
          </div>

          {/* Deployment Identity — read-only, set by QZ at onboarding */}
          {(form.product_name || form.env) && (
            <div className="flex flex-col gap-4">
              <SectionLabel>Deployment Identity</SectionLabel>

              {form.product_name && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                    Product <span className="font-normal text-ink-gray-400">(read-only)</span>
                  </label>
                  <Input type="text" value={form.product_name} disabled />
                </div>
              )}

              {form.marketplace_product_id && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                    Marketplace Product ID <span className="font-normal text-ink-gray-400">(read-only)</span>
                  </label>
                  <Input type="text" value={form.marketplace_product_id} disabled />
                  <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                    GCP Marketplace service endpoint. Immutable once set in Producer Portal.
                  </p>
                </div>
              )}

              {form.offer_type && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                    Offer Type <span className="font-normal text-ink-gray-400">(read-only)</span>
                  </label>
                  <Input type="text" value={form.offer_type} disabled />
                </div>
              )}

              {form.env && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                    Environment <span className="font-normal text-ink-gray-400">(read-only)</span>
                  </label>
                  <Input
                    type="text"
                    value={form.env === "custom" && form.env_label ? `custom — ${form.env_label}` : form.env}
                    disabled
                  />
                  <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                    Only <code>prd</code> and <code>custom</code> environments publish billable telemetry.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Reporting toggle — customer-editable */}
          <div className="flex flex-col gap-4">
            <SectionLabel>Reporting Control</SectionLabel>

            <div className="flex items-center justify-between gap-4 py-1">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                  Enable Reporting
                </span>
                <span className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                  When off, the daily reporting job publishes nothing. Usage from the disabled period is not sent later.
                </span>
              </div>
              <Toggle
                enabled={form.reporting_enabled}
                onChange={(v) => setForm((prev) => ({ ...prev, reporting_enabled: v }))}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || form.reporting_enabled === originalReportingEnabled}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ConfigTelemetryPage;
